import { useState } from 'react';
import { defaultDateByRule, listDueRules, workMonthOf } from '@/fmt/generate-tasks';
import { formatYearMonth, weekdayLabel } from '@/tasks/calendar-range';
import type { MonthlyFmtTask } from '@cortex/ado-task-fmt';

/**
 * MonthlyRunDialog の props。
 */
export interface MonthlyRunDialogProps {
  /** 対象月（`YYYY-MM`） */
  month: string;
  /** 作成対象の月次経理タスク */
  items: readonly MonthlyFmtTask[];
  /** 作成するときに呼ばれる。予定営業日 → 対応日の対応表を渡す */
  onCreate: (dateByRule: Record<string, string>) => void;
  /** 閉じるときに呼ばれる */
  onClose: () => void;
  /** 対象月の後ろに付ける言葉。月次は「分」、決算は「決算分」 */
  periodSuffix?: string;
}

/** @graph-connects none */
const DATE_CLASS =
  'rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-2 py-1 text-sm tabular-nums text-[var(--color-text-primary)]';

/**
 * @graph-node {Page}
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business タスクを作る前に、予定営業日（1営業日・10営業日・毎月25日など）が今月の何日かを指定するポップアップ。土日は自動で除くが祝日は除けないため、ここで人が直してから作成する
 * @graph-connects listDueRules [calls] 指定が必要な予定営業日を並べる
 * @graph-connects defaultDateByRule [calls] 既定の日付を下書きする
 * @graph-connects workMonthOf [calls] 締める月を出す
 * @graph-connects weekdayLabel [calls] 曜日を出す
 */
export function MonthlyRunDialog({
  month,
  items,
  onCreate,
  onClose,
  periodSuffix = '分',
}: MonthlyRunDialogProps) {
  const rules = listDueRules(items);
  const [dates, setDates] = useState<Record<string, string>>(defaultDateByRule(month, rules));

  const countOf = (rule: string): number => items.filter((item) => item.dueRule === rule).length;

  return (
    <>
      <button
        type="button"
        aria-label="作成をやめる"
        onClick={onClose}
        className="fixed inset-0 z-10 cursor-default bg-[var(--color-text-primary)]/20"
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="タスクの作成"
        className="fixed top-1/2 left-1/2 z-20 flex max-h-[85vh] w-[min(34rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-6 py-5 shadow-[0_8px_32px_var(--color-shadow)]"
      >
        <div className="mb-1 flex items-start justify-between gap-3">
          <h2 className="m-0 text-base font-semibold text-[var(--color-text-primary)]">
            {formatYearMonth(month)}
            {periodSuffix}のタスクを作成
          </h2>
          <button
            type="button"
            aria-label="閉じる"
            onClick={onClose}
            className="-mt-1 shrink-0 px-1 text-lg leading-none text-[var(--color-text-muted)]"
          >
            ×
          </button>
        </div>
        <p className="mt-0 mb-4 text-xs leading-relaxed text-[var(--color-text-secondary)]">
          {formatYearMonth(month)}
          {periodSuffix}を締めるのは{formatYearMonth(workMonthOf(month))}
          です。その営業日が何日かを確認してください。土日は自動で外していますが、祝日は
          見ていないのでここで直してください。ここで決めた日付がタスクの対応日になります。
        </p>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border-subtle)] bg-[var(--color-bg-subtle)]">
                <th className="py-1.5 pr-3 font-medium text-[var(--color-text-muted)]">予定</th>
                <th className="w-12 py-1.5 pr-3 text-right font-medium text-[var(--color-text-muted)]">
                  件数
                </th>
                <th className="w-44 py-1.5 font-medium text-[var(--color-text-muted)]">対応日</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => {
                // 人が日付を消すこともある（まだ決まっていない営業日）
                const date = dates[rule] ?? '';
                const weekday = date === '' ? '' : weekdayLabel(date);
                const isWeekend = weekday === '土' || weekday === '日';
                return (
                  <tr key={rule} className="border-b border-[var(--color-border-subtle)]">
                    <td className="py-1.5 pr-3 whitespace-nowrap text-[var(--color-text-primary)]">
                      {rule}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-[var(--color-text-muted)]">
                      {countOf(rule)}
                    </td>
                    <td className="py-1.5">
                      <span className="flex items-center gap-2">
                        <input
                          type="date"
                          aria-label={`${rule}の対応日`}
                          value={date}
                          onChange={(event) => {
                            setDates({ ...dates, [rule]: event.target.value });
                          }}
                          className={DATE_CLASS}
                        />
                        {/* 土日に当たっているものは色を変えて気づけるようにする */}
                        <span
                          className={
                            isWeekend
                              ? 'text-xs text-[var(--color-overdue)]'
                              : 'text-xs text-[var(--color-text-muted)]'
                          }
                        >
                          {weekday === '' ? '' : `（${weekday}）`}
                        </span>
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-5 flex items-center justify-between gap-3">
          <span className="text-xs text-[var(--color-text-muted)]">
            {items.length} 件のタスクを作成します
          </span>
          <span className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-3 py-1.5 text-sm text-[var(--color-text-secondary)]"
            >
              取りやめる
            </button>
            <button
              type="button"
              onClick={() => {
                onCreate(dates);
              }}
              className="rounded bg-[var(--color-accent)] px-3 py-1.5 text-sm font-medium text-[var(--color-bg-surface)]"
            >
              作成する
            </button>
          </span>
        </div>
      </aside>
    </>
  );
}
